import { createDefaultAuthConfig, resolveAuthOrigin, type AuthConfig } from "@stenpro/auth";
import { resolveRuntimeValue, runtimeConfig } from "@/runtimeConfig";

export const DEFAULT_TENANT =
  resolveRuntimeValue(runtimeConfig?.tenantId, import.meta.env.VITE_PROJECT_TENANT) ??
  "demo-tenant";
export const DEFAULT_ORGANIZATION =
  resolveRuntimeValue(runtimeConfig?.organizationId, import.meta.env.VITE_PROJECT_ORGANIZATION) ??
  "demo-org";
// Default to the canonical demo user id from DemoDataRegistry to match Project demo seeding.
export const DEFAULT_ACTOR =
  resolveRuntimeValue(runtimeConfig?.actorId, import.meta.env.VITE_PROJECT_ACTOR) ??
  "00000000-0000-0000-0000-000000000101";
export const DEFAULT_COMPANY =
  resolveRuntimeValue(runtimeConfig?.companyId, import.meta.env.VITE_COMPANY_ID) ?? "SE-100";

export function createAuthConfig(): AuthConfig {
  const envMode = resolveRuntimeValue(
    runtimeConfig?.authMode,
    import.meta.env.VITE_AUTH_MODE,
  ) as AuthConfig["mode"] | undefined;
  const authMode = envMode ?? "stub";
  const authority =
    resolveRuntimeValue(runtimeConfig?.authAuthority, import.meta.env.VITE_AUTH_AUTHORITY) ??
    "https://auth.stenpro.local";
  const audience =
    resolveRuntimeValue(runtimeConfig?.authAudience, import.meta.env.VITE_AUTH_AUDIENCE) ??
    "project-api";
  const clientId =
    resolveRuntimeValue(runtimeConfig?.authClientId, import.meta.env.VITE_AUTH_CLIENT_ID) ??
    "project-frontend-local";
  const origin = resolveAuthOrigin(
    resolveRuntimeValue(runtimeConfig?.siteOrigin, import.meta.env.VITE_SITE_ORIGIN),
  );
  const redirectUri =
    resolveRuntimeValue(runtimeConfig?.authRedirectUri, import.meta.env.VITE_AUTH_REDIRECT_URI) ??
    `${origin}/auth/callback`;
  const postLogoutRedirectUri =
    resolveRuntimeValue(
      runtimeConfig?.authPostLogoutRedirectUri,
      import.meta.env.VITE_AUTH_POST_LOGOUT_REDIRECT_URI,
    ) ?? origin;
  const scopes =
    authMode === "oidc"
      ? ["openid", "profile", "email"]
      : ["openid", "profile", "project.read"];

  const baseConfig = createDefaultAuthConfig({
    authority,
    audience,
    clientId,
    tenantId: DEFAULT_TENANT,
    organizationId: DEFAULT_ORGANIZATION,
    actorId: DEFAULT_ACTOR,
    appName: "Project",
    // Defaults for demo/dev usability. The Project API's "ProjectOwner" policy allows:
    // - project.owner
    // - Administrator
    // - ProjectManager
    roles: ["Administrator", "project.owner", "project.user"],
    scopes,
    siteOrigin: origin,
  });

  return {
    ...baseConfig,
    mode: authMode,
    authority,
    clientId,
    redirectUri,
    postLogoutRedirectUri,
    defaultScopes: scopes,
  };
}
